/** An entry paired with the party it belongs to, or null when it has none on the matter */
export interface ContactEntryPair<E, C> {
    entry: E;
    contact: C | null;
}

export interface ContactPairing<E, C> {
    /** Every entry, in entry order */
    pairs: ContactEntryPair<E, C>[];
    /** The parties no entry belongs to, in party order */
    unpairedContacts: C[];
}

/**
 * Pairs stored or submitted contact entries (contact_form_values, an intake's contacts) with a
 * matter's parties. An entry with a contactId is that party's. An entry without one, such as one
 * saved before entries carried it, is the party at its position, as long as no entry with an id
 * is theirs. So an entry saved to a new party is paired with that party's row the next time.
 *
 * An entry is left unpaired, rather than given to someone else, when its contactId isn't one of
 * the parties, or an earlier entry already has that party.
 */
export function pairEntriesWithContacts<
    E extends { contactId?: string | null },
    C extends { contactId: string },
>(entries: readonly E[], contacts: readonly C[]): ContactPairing<E, C> {
    const contactsById = new Map(contacts.map((contact) => [contact.contactId, contact]));
    const claimedIds = new Set<string>();

    // Entries with an id first, so a positional match can't take a party who has their own entry
    const pairedById = entries.map((entry) => {
        const contact = entry.contactId ? contactsById.get(entry.contactId) : undefined;
        if (!contact || claimedIds.has(contact.contactId)) return null;
        claimedIds.add(contact.contactId);
        return contact;
    });

    const pairs = entries.map((entry, index): ContactEntryPair<E, C> => {
        const byId = pairedById[index];
        if (byId) return { entry, contact: byId };
        if (entry.contactId) return { entry, contact: null };

        const atPosition = index < contacts.length ? contacts[index] : undefined;
        if (!atPosition || claimedIds.has(atPosition.contactId)) return { entry, contact: null };
        claimedIds.add(atPosition.contactId);
        return { entry, contact: atPosition };
    });

    return {
        pairs,
        unpairedContacts: contacts.filter((contact) => !claimedIds.has(contact.contactId)),
    };
}
